import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@hulahoop/ui";
import { type ReactNode, useRef } from "react";
import type { Option } from "@/domain/types/option";

function useStableOptions<V extends string>(options: readonly Option<V>[]) {
  const key = options.map((option) => `${option.value}\u0000${option.label}`).join("\u0001");
  const cache = useRef({ key, options });
  if (cache.current.key !== key) cache.current = { key, options };
  return cache.current.options;
}

export function OptionSelect<V extends string>({
  value,
  onValueChange,
  placeholder,
  options,
  disabled,
  label,
  renderOption,
}: {
  value: V | null;
  onValueChange: (value: V | null) => void;
  placeholder?: string;
  options: readonly Option<V>[];
  disabled?: boolean;
  label?: string;
  renderOption?: (option: Option<V>) => ReactNode;
}) {
  const items = useStableOptions(options);
  return (
    <Select
      value={value}
      onValueChange={(next) =>
        onValueChange(items.find((option) => option.value === next)?.value ?? null)
      }
      items={items}
      disabled={disabled}
    >
      <SelectTrigger className="w-full" aria-label={label}>
        {renderOption ? (
          <SelectValue placeholder={placeholder}>
            {(current) => {
              const option = items.find((candidate) => candidate.value === current);
              return option ? renderOption(option) : null;
            }}
          </SelectValue>
        ) : (
          <SelectValue placeholder={placeholder} />
        )}
      </SelectTrigger>
      <SelectContent>
        {items.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {renderOption ? renderOption(option) : option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
